import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionNum, PrimaryCTA } from "@/components/ui-bits";
import { VISIBLE_PRACTICE_AREAS } from "@/data/practiceAreas";
import { ArrowUpRight } from "lucide-react";

export default function Services() {
  return (
    <Layout>
      <SEO
        title="Practice Areas"
        description="Nine practice areas at Shelton Law PLLC: business transactions, international trade, export compliance, CFIUS, FOCI, government contracts, intellectual property, FCC, and insurance."
        path="/services"
      />
      <section className="border-b border-hairline" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-20">
          <Eyebrow>Practice Areas</Eyebrow>
          <h1 className="display-1 text-ink mt-6 max-w-4xl">
            Practice areas.
          </h1>
          <p className="mt-8 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
            Engagements range from a single negotiated agreement to ongoing representation as outside general counsel.
          </p>
          <p className="mt-5 text-ink-soft italic font-serif text-lg leading-snug max-w-2xl" data-testid="services-co-counsel-note">
            Some matters are handled in association with co-counsel selected for the specific issue.
          </p>
        </div>
      </section>

      <section data-testid="services-areas">
        {VISIBLE_PRACTICE_AREAS.map((p, idx) => (
          <div
            key={p.slug}
            className={`border-b border-hairline ${idx % 2 === 1 ? "bg-ivory-alt" : "bg-ivory"}`}
            data-testid={`services-area-${p.slug}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-4">
                <SectionNum>{p.number}</SectionNum>
                <h2 className="display-2 text-ink mt-3 leading-tight">{p.name}</h2>
                <p className="mt-5 text-ink-soft italic font-serif text-xl leading-snug">{p.kicker}</p>
                <div className="mt-8">
                  <Link
                    to={`/services/${p.slug}`}
                    className="inline-flex items-center gap-2 text-ink border-b border-brass pb-1 hover:text-brass transition-colors text-[11px] tracking-[0.24em] uppercase font-semibold"
                    data-testid={`services-area-link-${p.slug}`}
                  >
                    Full Description <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-7 lg:col-start-6">
                <p className="text-ink text-[1.0625rem] leading-relaxed mb-8">{p.lead}</p>
                <div className="border-t border-hairline">
                  {p.sections.map((s) => (
                    <div key={s.h} className="border-b border-hairline py-5">
                      <h3 className="font-serif text-xl text-ink leading-tight">{s.h}</h3>
                      <p className="mt-2 text-ink-soft text-[0.95rem] leading-relaxed">{s.p}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-ink text-ivory" data-testid="services-cta">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-20 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-5">Engagement Inquiries</p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight max-w-3xl mx-auto">
            For an initial consultation, contact the firm directly.
          </h2>
          <div className="mt-10 flex justify-center">
            <PrimaryCTA to="/contact" testId="services-cta-button">Contact the Firm</PrimaryCTA>
          </div>
        </div>
      </section>
    </Layout>
  );
}
